import { API_DGAMES } from '@/configs';
import {
  ICategory,
  ICategoryItem,
  ICategoryNameAPI,
  IJackpotGameListResponse,
  IJackpotLeaderboardPlayerResponse,
  IJackpotLeaderboardResponse,
  IJackpotResponse,
  ILauncher,
  ILeaderboardItem,
  IUserGameInfo,
  IUserGameProgress,
} from '@/interfaces/api/gamefi';
import createAxiosInstance from '@/services/http-client';

const gameFiClient = createAxiosInstance({
  baseURL: API_DGAMES,
});

const getLauncher = async () => {
  try {
    return (
      (((await gameFiClient.get(`/v1/game-config/launcher`)) as any)
        ?.config as ILauncher) || []
    );
  } catch (error) {
    throw error;
  }
};

const getCategoryItems = async (categoryId: string) => {
  return (
    ((
      (await gameFiClient.get(
        `/v1/web-game-config/configs/${categoryId}`
      )) as any
    )?.configs as ICategoryItem[]) || []
  );
};

const getTrendingCategoryItems = async () => {
  return (
    (((await gameFiClient.get(`/v1/web-game-config/config/trending`)) as any)
      ?.configs as ICategoryItem[]) || []
  );
};

const getCategoryName = async () => {
  return (
    (((await gameFiClient.get('/v1/web-game-config/categories')) as any)
      ?.categories as ICategoryNameAPI[]) || []
  );
};

const getCategories = async () => {
  try {
    const categoriesName = await getCategoryName();

    const categories: ICategory[] = [];
    for (const item of categoriesName) {
      const { id: categoryId } = item;
      const categoryItems = await getCategoryItems(categoryId);
      categories.push({ ...item, items: categoryItems });
    }
    return categories;
  } catch (error) {
    throw error;
  }
};

const getCategoryItem = async (gameId: string) => {
  try {
    return (
      (await gameFiClient.get(
        `/v1/web-game-config/config/gameId/${gameId}`
      )) as any
    ).config as ICategoryItem;
  } catch (error) {
    throw error;
  }
};

const getLeaderBoardItems = async (page: number, limit: number) => {
  try {
    return (
      (await gameFiClient.get(
        `/v1/async-games/earning/leaderboard?page=${page}&limit=${limit}`
      )) as any
    ).leaderboard as ILeaderboardItem[];
  } catch (error) {
    throw error;
  }
};

const submitReferral = async ({
  inviter,
  invitee,
}: {
  inviter: string;
  invitee: string;
}) => {
  try {
    await gameFiClient.put('/admin/v1/dgames/invitation', {
      inviter,
      invitee,
    });
  } catch (error) {
    throw new Error('Submit referral address error.');
  }
};

const trackingWithdrawal = ({
  from,
  to,
  amount,
}: {
  from: string;
  to: string;
  amount: string;
}) => {
  try {
    gameFiClient.post('/admin/v1/tracking/withdraw', {
      from,
      to,
      amount,
    });
  } catch (e) {
    // TODO handle error
  }
};

const getUserGameInfo = async (address: string) => {
  try {
    return (await gameFiClient.get(
      `/v1/user/info/${address}`
    )) as any as IUserGameInfo;
  } catch (error) {
    throw error;
  }
};

const getUserGameProgress = async (address: string) => {
  try {
    return (await gameFiClient.get(
      `/v1/user/progress/${address}`
    )) as any as IUserGameProgress;
  } catch (error) {
    throw error;
  }
};

export const getJackpotInfo = async (): Promise<IJackpotResponse> => {
  try {
    const res = (await gameFiClient.get(
      `v1/async-games/jackpot/current`
    )) as IJackpotResponse;
    return res;
  } catch (err: unknown) {
    return { jackpot: {} as any };
  }
};

export const getJackpotLeaderboard =
  async (): Promise<IJackpotLeaderboardResponse> => {
    try {
      const res = (await gameFiClient.get(
        `v1/async-games/jackpot/leaderboard`
      )) as IJackpotLeaderboardResponse;
      return res;
    } catch (err: unknown) {
      return { leaderboard: [] };
    }
  };

export const getJackpotLeaderboardPlayer = async (
  address: string
): Promise<IJackpotLeaderboardPlayerResponse> => {
  try {
    const res = (await gameFiClient.get(
      `v1/async-games/jackpot/player/info/${address}`
    )) as IJackpotLeaderboardPlayerResponse;
    return res;
  } catch (err: unknown) {
    throw err;
  }
};

const getIsBlockCountryIP = async () => {
  try {
    return ((await gameFiClient.get('/v1/network/is-block')) as any)
      .block as boolean;
  } catch (error) {
    throw error;
  }
};

export const getGameList = async (): Promise<IJackpotGameListResponse> => {
  try {
    const res = (await gameFiClient.get(
      `v1/async-games/jackpot/games`
    )) as IJackpotGameListResponse;
    return res;
  } catch (err: unknown) {
    return { games: [] };
  }
};

export {
  getCategories,
  getCategoryItem,
  getIsBlockCountryIP,
  getLauncher,
  getLeaderBoardItems,
  getTrendingCategoryItems,
  getUserGameInfo,
  getUserGameProgress,
  submitReferral,
  trackingWithdrawal,
};
